import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { toast } from "sonner";
import BreathOrb from "../breathwork/BreathOrb";
import { Textarea } from "@/components/ui/textarea";
import {
  ChevronLeft, X, Loader2, CheckCircle2,
  Briefcase, Home, Users, Heart, User, Activity, Wallet, GraduationCap, MoreHorizontal,
} from "lucide-react";

const TOTAL = 4;
const BASE_SITUATIONS = [
  { id: "work", icon: Briefcase }, { id: "private", icon: Home }, { id: "public", icon: Users },
  { id: "togetherness", icon: Heart }, { id: "family", icon: User }, { id: "health", icon: Activity },
  { id: "financial", icon: Wallet }, { id: "learning", icon: GraduationCap }, { id: "other", icon: MoreHorizontal },
];
const PHASE_ORDER = ["inhale", "hold_in", "exhale", "hold_out"];

function patternToPhases(tech) {
  const p = tech?.pattern;
  if (!p) return { phases: [{ key: "inhale", seconds: 4 }, { key: "exhale", seconds: 6 }], rounds: 6 };
  const phases = PHASE_ORDER.filter((k) => p[k]).map((k) => ({ key: k, seconds: p[k] }));
  const cycle = phases.reduce((a, b) => a + b.seconds, 0) || 18;
  const rounds = Math.max(3, Math.min(8, Math.round((tech.duration_seconds || 240) / cycle)));
  return { phases: phases.length ? phases : [{ key: "inhale", seconds: 4 }, { key: "exhale", seconds: 6 }], rounds };
}

function Scale({ value, onChange, prefix }) {
  return (
    <div className="grid grid-cols-10 gap-1.5">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
        <button key={n} onClick={() => onChange(n)} data-testid={`${prefix}-${n}`}
          className={`flex h-10 items-center justify-center rounded-lg border text-sm font-semibold transition-colors ${value === n ? "border-primary bg-primary text-primary-foreground" : "border-border text-foreground hover:bg-accent"}`}>{n}</button>
      ))}
    </div>
  );
}

export default function BreakoutSession() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("breakout_start_session"));

  const [step, setStep] = useState(0);
  const [emotions, setEmotions] = useState([]);
  const [emotion, setEmotion] = useState("");
  const [baseSituation, setBaseSituation] = useState("");
  const [intensity, setIntensity] = useState(5);
  const [technique, setTechnique] = useState(null);
  const [session, setSession] = useState(null);
  const [after, setAfter] = useState(null);
  const [situation, setSituation] = useState("");
  const [belief, setBelief] = useState("");
  const [busy, setBusy] = useState(false);
  const [run, setRun] = useState({ completed: false, elapsed_seconds: 0 });

  useEffect(() => { api.get("/emotions").then(({ data }) => setEmotions(data || [])).catch(() => setEmotions([])); }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/breakout/start", {
        emotion, situation: "", belief: "",
        base_situation: baseSituation || undefined,
        intensity_before: intensity, source: "manual",
      });
      setTechnique(data.technique);
      setSession(data.session);
      setStep(3);
    } catch (e) {
      toast.error(apiError(e, t("something_went_wrong")));
    } finally { setBusy(false); }
  };

  const complete = async () => {
    if (!session || after === null) return;
    setBusy(true);
    try {
      await api.post("/breakout/complete", {
        session_id: session.id, intensity_after: after,
        technique_id: technique?.id, situation: situation.trim(), belief: belief.trim(),
      });
      setStep(6);
    } catch (e) {
      toast.error(apiError(e, t("something_went_wrong")));
    } finally { setBusy(false); }
  };

  const onBack = () => {
    if (step === 0) navigate("/app/breakout");
    else if (step >= 5) navigate("/app/breakout");
    else setStep(Math.max(0, step - 1));
  };

  if (step === 4 && technique) {
    const { phases, rounds } = patternToPhases(technique);
    return (
      <div className="mx-auto max-w-xl" data-testid="breakout-player">
        <BreathOrb phases={phases} rounds={rounds} techniqueLabel={technique.name}
          onComplete={(r) => { setRun(r); setStep(5); }} onCancel={() => setStep(5)} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-5" data-testid="breakout-session-screen">
      <div className="flex items-center gap-3">
        <button onClick={onBack} data-testid="breakout-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent">
          {step === 0 ? <X className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
        </button>
        {step < 4 ? (
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-secondary">
            <div className="h-full bg-primary transition-all" style={{ width: `${((step + 1) / TOTAL) * 100}%` }} />
          </div>
        ) : <h1 className="font-display text-xl text-foreground">{t("breakout")}</h1>}
      </div>

      {step === 0 ? (
        <div className="space-y-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("step_of", { n: 1, total: TOTAL })}</p>
          <h2 className="font-display text-2xl text-foreground">{t("what_feeling")}</h2>
          <div className="flex flex-wrap gap-2">
            {emotions.map((e) => {
              const on = emotion === e.id;
              return (
                <button key={e.id} onClick={() => setEmotion(e.id)} data-testid={`emotion-${e.id}`}
                  className={`flex items-center gap-2 rounded-full border px-3 py-2 text-sm capitalize transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-accent"}`}>
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: e.color }} />{e.label}
                </button>
              );
            })}
          </div>
          <button onClick={() => setStep(1)} disabled={!emotion} data-testid="breakout-continue-0" className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-40">{t("continue")}</button>
        </div>
      ) : null}

      {step === 1 ? (
        <div className="space-y-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("step_of", { n: 2, total: TOTAL })}</p>
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("base_situation")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("base_situation_hint")}</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {BASE_SITUATIONS.map((b) => {
              const on = baseSituation === b.id;
              return (
                <button key={b.id} onClick={() => setBaseSituation(on ? "" : b.id)} data-testid={`base-${b.id}`}
                  className={`flex aspect-square flex-col items-center justify-center gap-2 rounded-xl border p-2 transition-colors ${on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground hover:bg-accent"}`}>
                  <b.icon className="h-6 w-6" />
                  <span className="text-xs font-medium">{t(`bs_${b.id}`)}</span>
                </button>
              );
            })}
          </div>
          <button onClick={() => setStep(2)} data-testid="breakout-continue-1" className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground">{t("continue")}</button>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="space-y-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("step_of", { n: 3, total: TOTAL })}</p>
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("how_intense")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("intensity_scale_hint")}</p>
          </div>
          <p className="text-center font-display text-6xl text-primary">{intensity}</p>
          <Scale value={intensity} onChange={setIntensity} prefix="intensity" />
          <p className="text-xs italic text-muted-foreground">{t("situation_belief_after_hint")}</p>
          <button onClick={start} disabled={busy} data-testid="breakout-continue-2" className="flex w-full items-center justify-center rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-40">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("continue")}
          </button>
        </div>
      ) : null}

      {step === 3 && technique ? (
        <div className="space-y-4">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">{t("step_of", { n: 4, total: TOTAL })}</p>
          <h2 className="font-display text-2xl text-foreground">{t("try_technique", { name: technique.name })}</h2>
          <p className="text-sm text-muted-foreground">{technique.description}</p>
          <div className="space-y-3 rounded-2xl border border-border bg-card p-4">
            <Row label={t("how_it_works_row")} v={technique.how_it_works} />
            <Row label={t("expected")} v={technique.expected_effect} />
            <Row label={t("duration")} v={technique.duration_seconds ? `${Math.round(technique.duration_seconds / 60)} ${t("min")}` : ""} />
            <Row label={t("caution")} v={technique.contraindications} />
          </div>
          <button onClick={() => setStep(4)} data-testid="breakout-begin-breathing" className="w-full rounded-full bg-primary py-3 font-semibold text-primary-foreground">{t("begin_breathing")}</button>
        </div>
      ) : null}

      {step === 5 ? (
        <div className="space-y-5">
          <div>
            <h2 className="font-display text-2xl text-foreground">{t("how_intense")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("intensity_scale_hint")}</p>
          </div>
          <Scale value={after} onChange={setAfter} prefix="after" />
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("base_situation")}</p>
            <Textarea value={situation} onChange={(e) => setSituation(e.target.value)} maxLength={2000} className="min-h-[80px]" data-testid="breakout-situation" />
          </div>
          <button onClick={complete} disabled={busy || after === null} data-testid="breakout-complete" className="flex w-full items-center justify-center rounded-full bg-primary py-3 font-semibold text-primary-foreground disabled:opacity-40">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("session_done_btn")}
          </button>
        </div>
      ) : null}

      {step === 6 ? (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/15"><CheckCircle2 className="h-10 w-10 text-primary" /></span>
          <h2 className="font-display text-2xl text-foreground">{t("session_success_title")}</h2>
          <p className="max-w-sm text-sm text-muted-foreground">{t("session_success_body")}</p>
          <button onClick={() => navigate("/app/breakout")} data-testid="breakout-success-done" className="rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground">{t("session_done_btn")}</button>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, v }) {
  if (!v) return null;
  return (
    <div className="space-y-0.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm leading-relaxed text-foreground">{v}</p>
    </div>
  );
}
